import { getBrowserExtensionId } from '@/config/env-config';
import { SskFailureBucket, SskSigningError } from '@/utils/ssk-failure';

export interface ExtensionSigningResult {
  sigma2Bytes: Uint8Array;
  modulus: string;
  publicExponent: number;
}

interface ExtensionResponse {
  action: string;
  status: 'success' | 'error';
  sigma2?: string;
  modulus?: string;
  publicExponent?: number;
  error?: string;
  code?: string;
}

const EXTENSION_TIMEOUT_MS = 10_000;

// The extension's ConfigErrorCode values: no policy, unmanaged browser, or a
// policy read failure all mean this device holds no share.
const NO_SHARE_CONFIG_CODES = new Set([
  'MISSING_FIELDS',
  'NOT_AVAILABLE',
  'CHROME_ERROR',
]);

// The key share never leaves the extension; it returns sigma2 for the hex digest.
export function requestExtensionSigning(
  digestHex: string
): Promise<ExtensionSigningResult> {
  const extensionId = getBrowserExtensionId();

  // Safari has no `chrome` global; bare reference (even with `?.`) throws ReferenceError.
  if (typeof chrome === 'undefined') {
    return Promise.reject(
      new SskSigningError(
        SskFailureBucket.UNSUPPORTED_BROWSER,
        'No chrome runtime in this browser'
      )
    );
  }

  if (!extensionId || !chrome.runtime?.sendMessage) {
    return Promise.reject(
      new SskSigningError(
        SskFailureBucket.NO_SHARE,
        'NewCore browser extension is not installed'
      )
    );
  }

  const signingPromise = new Promise<ExtensionSigningResult>(
    (resolve, reject) => {
      chrome.runtime.sendMessage(
        extensionId,
        { action: 'signAssertion', signedInfoHash: digestHex },
        (response: ExtensionResponse) => {
          if (chrome.runtime.lastError) {
            reject(
              new SskSigningError(
                SskFailureBucket.NO_SHARE,
                `Extension unreachable: ${chrome.runtime.lastError.message}`
              )
            );

            return;
          }

          if (!response || response.status === 'error') {
            reject(
              new SskSigningError(
                NO_SHARE_CONFIG_CODES.has(response?.code ?? '')
                  ? SskFailureBucket.NO_SHARE
                  : SskFailureBucket.UNKNOWN,
                `Extension signing error: ${response?.error ?? 'no response'}`
              )
            );

            return;
          }

          if (
            !response.sigma2 ||
            !response.modulus ||
            !response.publicExponent
          ) {
            reject(
              new SskSigningError(
                SskFailureBucket.UNKNOWN,
                'Extension returned an incomplete signing response'
              )
            );

            return;
          }

          const binaryString = atob(response.sigma2);
          const sigma2Bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            sigma2Bytes[i] = binaryString.charCodeAt(i);
          }

          resolve({
            sigma2Bytes,
            modulus: response.modulus,
            publicExponent: response.publicExponent,
          });
        }
      );
    }
  );

  const timeout = new Promise<never>((_, reject) =>
    setTimeout(
      () =>
        reject(
          new SskSigningError(
            SskFailureBucket.TIMEOUT,
            `Extension did not respond within ${EXTENSION_TIMEOUT_MS}ms`
          )
        ),
      EXTENSION_TIMEOUT_MS
    )
  );

  return Promise.race([signingPromise, timeout]);
}
